import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Tabs,
  Table,
  Button,
  Space,
  Modal,
  Drawer,
  Input,
  InputNumber,
  Switch,
  Form,
  Tooltip,
  message,
  Skeleton,
  Empty,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  TableOutlined,
  ExperimentOutlined,
  ApartmentOutlined,
  ToolOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import {
  listScenarioCategoriesAdmin,
  upsertScenarioCategory,
  retireScenarioCategory,
  listAgentCategories,
  addAgentCategory,
  updateAgentCategory,
  removeAgentCategory,
  listSkillCategoryUsage,
  renameSkillCategory,
  type ScenarioCategoryRow,
  type AgentCategoryRow,
  type SkillCategoryUsage,
} from '@/api/lookups';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Lookups admin (Platform → Lookups)
// ─────────────────────────────────────────────────────────────────────────────
//
// Single page, three tabs:
//
//   1. Scenario categories — full CRUD over aivalidation.ScenarioCategory.
//      Composite key (Domain, Code). Soft-retire (IsActive=false), no
//      hard delete because historical scenarios may still reference the code.
//
//   2. Agent categories — full CRUD over ai.AiAgentCategory via the
//      existing BaseSqlCrudApiController endpoints.
//
//   3. Skill categories — derived view (free string column, not a real
//      lookup table). Shows distinct values + usage counts; lets an admin
//      rename across rows to fix typos / consolidate.
// ─────────────────────────────────────────────────────────────────────────────

export const LookupsPage = observer(function LookupsPage() {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<TableOutlined />}
        title="Lookups"
        subtitle="Reference values used across Agent Studio. Pick a tab to manage one taxonomy."
      />

      <SectionCard noPadding>
        <Tabs
          defaultActiveKey="scenario"
          tabBarStyle={{ padding: '0 18px', marginBottom: 0 }}
          items={[
            {
              key: 'scenario',
              label: <span><ExperimentOutlined /> Scenario categories</span>,
              children: (
                <div style={{ padding: '14px 18px 24px' }}>
                  <ScenarioCategoryTab isDark={isDark} />
                </div>
              ),
            },
            {
              key: 'agent',
              label: <span><ApartmentOutlined /> Agent categories</span>,
              children: (
                <div style={{ padding: '14px 18px 24px' }}>
                  <AgentCategoryTab isDark={isDark} />
                </div>
              ),
            },
            {
              key: 'skill',
              label: <span><ToolOutlined /> Skill categories</span>,
              children: (
                <div style={{ padding: '14px 18px 24px' }}>
                  <SkillCategoryTab isDark={isDark} />
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
});

// ── Tab 1: Scenario categories (true CRUD) ─────────────────────────────────

function ScenarioCategoryTab({ isDark: _isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<ScenarioCategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ScenarioCategoryRow | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      setRows(await listScenarioCategoriesAdmin());
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const cols: ColumnsType<ScenarioCategoryRow> = [
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      width: 130,
      render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 90,
      render: (v: string) => (
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            fontSize: 12,
            background: colors.violet[600],
            color: '#FFFFFF',
            padding: '2px 7px',
            borderRadius: 4,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: 'Display name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (v: string) => <Text style={{ fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v?: string | null) => <Text type="secondary" style={{ fontSize: 12.5 }}>{v ?? '—'}</Text>,
    },
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'right',
      render: (v: number) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{v}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v: boolean) =>
        v ? <StatusPill tone="green">Active</StatusPill> : <StatusPill tone="slate">Retired</StatusPill>,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_t, row) => (
        <Space size={2}>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(row)} />
          </Tooltip>
          {row.isActive && (
            <Tooltip title="Retire (soft delete)">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: `Retire "${row.code}"?`,
                    content: `${row.displayName} will be hidden from the filter dropdown. Existing scenarios still reference the code; you can re-activate it later if needed.`,
                    okText: 'Retire',
                    okButtonProps: { danger: true },
                    onOk: async () => {
                      try {
                        await retireScenarioCategory(row.domain, row.code);
                        void message.success('Retired');
                        await refresh();
                      } catch (err) {
                        void message.error((err as Error).message);
                      }
                    },
                  });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Space style={{ justifyContent: 'space-between', display: 'flex' }}>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          Per-domain taxonomy used by the Scenario library filter bar. Populated by migrations; manage rows here.
        </Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void refresh()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Add category
          </Button>
        </Space>
      </Space>
      <Table<ScenarioCategoryRow>
        rowKey={(r) => `${r.domain}:${r.code}`}
        loading={loading}
        columns={cols}
        dataSource={rows}
        pagination={false}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description="No categories yet." /> }}
      />

      {creating && (
        <ScenarioCategoryEditor
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <ScenarioCategoryEditor
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function ScenarioCategoryEditor({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: ScenarioCategoryRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      open
      title={mode === 'create' ? 'Add scenario category' : `Edit ${initial?.domain} / ${initial?.code}`}
      okText="Save"
      onCancel={onClose}
      confirmLoading={saving}
      onOk={async () => {
        try {
          const values = await form.validateFields();
          setSaving(true);
          await upsertScenarioCategory({
            domain: values.domain,
            code: values.code,
            displayName: values.displayName,
            description: values.description,
            displayOrder: values.displayOrder ?? 0,
            isActive: values.isActive,
          });
          void message.success('Saved');
          await onSaved();
        } catch (err) {
          if ((err as { errorFields?: unknown[] }).errorFields) return; // antd validation
          void message.error((err as Error).message);
        } finally {
          setSaving(false);
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          initial ?? {
            domain: 'oncology',
            code: '',
            displayName: '',
            description: '',
            displayOrder: 0,
            isActive: true,
          }
        }
      >
        <Form.Item
          name="domain"
          label="Domain"
          tooltip="Vertical / area this taxonomy belongs to. Lowercase, hyphenated. Examples: oncology, rpm, finance."
          rules={[{ required: true, message: 'Domain is required' }]}
        >
          <Input disabled={mode === 'edit'} placeholder="oncology" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Code"
          tooltip="Short opaque identifier. Single letter (A..L) or short string. Used as the FK from ScenarioIndex."
          rules={[{ required: true, message: 'Code is required' }]}
        >
          <Input disabled={mode === 'edit'} placeholder="A" maxLength={8} />
        </Form.Item>
        <Form.Item name="displayName" label="Display name" rules={[{ required: true, message: 'Display name is required' }]}>
          <Input placeholder="Safety / critical" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="One-sentence description shown as a tooltip in the filter bar." />
        </Form.Item>
        <Form.Item name="displayOrder" label="Sort order" tooltip="Lower numbers appear first in the dropdown.">
          <InputNumber min={0} max={9999} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Tab 2: Agent categories (CRUD via BaseSqlCrudApiController) ────────────

function AgentCategoryTab({ isDark: _isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<AgentCategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<AgentCategoryRow | null>(null);
  const [creating, setCreating] = useState(false);
  // Client-side search — the lookup is small (typically < 50 rows), so a
  // round trip to the server per keystroke would be wasteful. Filters
  // both the displayed Name and Description columns with case-insensitive
  // substring matching.
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      setRows(await listAgentCategories());
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo<AgentCategoryRow[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const cols: ColumnsType<AgentCategoryRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => <Text style={{ fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v?: string | null) => <Text type="secondary" style={{ fontSize: 12.5 }}>{v ?? '—'}</Text>,
    },
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'right',
      render: (v?: number | null) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{v ?? '—'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v: boolean) =>
        v ? <StatusPill tone="green">Active</StatusPill> : <StatusPill tone="slate">Retired</StatusPill>,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_t, row) => (
        <Space size={2}>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(row)} />
          </Tooltip>
          {row.isActive && (
            <Tooltip title="Retire">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: `Retire "${row.name}"?`,
                    content: 'It will be hidden from agent grouping pickers. Agents currently assigned to it keep the link.',
                    okText: 'Retire',
                    okButtonProps: { danger: true },
                    onOk: async () => {
                      try {
                        await removeAgentCategory(row.id);
                        void message.success('Retired');
                        await refresh();
                      } catch (err) {
                        void message.error((err as Error).message);
                      }
                    },
                  });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Space style={{ justifyContent: 'space-between', display: 'flex' }}>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          Categories for grouping agents in the picker (e.g. "Oncology → Chemotherapy regimens").
        </Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void refresh()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Add category
          </Button>
        </Space>
      </Space>

      {/* Search bar — pinned above the table. Matches Name + Description
          (case-insensitive, substring). Match count surfaces beside the
          input so the user knows the table is filtered, not empty. */}
      <Space style={{ width: '100%' }} align="center">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 360 }}
        />
        {search.trim() && (
          <Text type="secondary" style={{ fontSize: 12.5 }}>
            {filtered.length} of {rows.length} match
          </Text>
        )}
      </Space>

      <Table<AgentCategoryRow>
        rowKey="id"
        loading={loading}
        columns={cols}
        dataSource={filtered}
        pagination={false}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <Empty
              description={
                search.trim()
                  ? `No categories match "${search.trim()}"`
                  : 'No agent categories yet.'
              }
            />
          ),
        }}
      />

      {creating && (
        <AgentCategoryEditor
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <AgentCategoryEditor
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function AgentCategoryEditor({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: AgentCategoryRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Drawer instead of Modal — per project UX convention ("prefer drawers
  // over modals for most interactive flows"). Drawer keeps the lookup
  // table visible behind the form so the operator stays oriented while
  // editing (which row in the list am I working on?), and gives us
  // breathing room if we ever expand the form to include color/icon/
  // parent-category fields.
  const handleSave = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (mode === 'create') {
        await addAgentCategory({
          name: values.name,
          description: values.description,
          displayOrder: values.displayOrder,
          isActive: values.isActive,
        });
      } else {
        await updateAgentCategory({
          id: initial!.id,
          name: values.name,
          description: values.description,
          displayOrder: values.displayOrder,
          isActive: values.isActive,
        });
      }
      void message.success('Saved');
      await onSaved();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      placement="right"
      width={520}
      title={mode === 'create' ? 'Add agent category' : `Edit "${initial?.name}"`}
      destroyOnClose
      maskClosable={!saving}
      closable={!saving}
      extra={
        <Space>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            Save
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initial ?? { name: '', description: '', displayOrder: 0, isActive: true }}
        onFinish={() => void handleSave()}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input placeholder="Chemotherapy regimens" autoFocus />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Optional — shown as a hint under the group label." />
        </Form.Item>
        <Form.Item name="displayOrder" label="Sort order">
          <InputNumber min={0} max={9999} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        {/* Hidden submit so Enter inside Name/Description fires the same
            handler the Save button uses, matching modal-form expectations. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </Form>
    </Drawer>
  );
}

// ── Tab 3: Skill categories (derived view + rename) ────────────────────────

function SkillCategoryTab({ isDark }: { isDark: boolean }) {
  const [rows, setRows] = useState<SkillCategoryUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [renameSource, setRenameSource] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setRows(await listSkillCategoryUsage());
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          padding: '10px 14px',
          background: isDark ? `${colors.amber[700]}15` : colors.amber[100],
          border: `1px solid ${colors.amber[600]}`,
          borderRadius: 8,
          fontSize: 12.5,
          color: isDark ? colors.amber[100] : colors.amber[700],
        }}
      >
        <b>Note —</b> Skill category is a free-string column on{' '}
        <code>ai.AiSkill</code>, not a true lookup table. Values below are
        derived from distinct rows in the table. To add a new value, edit a
        skill and type the new category. Use <b>Rename</b> below to fix typos
        or consolidate values across rows.
      </div>

      <Space style={{ justifyContent: 'space-between', display: 'flex' }}>
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          {rows.length === 0 ? 'No skills loaded.' : `${rows.length} distinct value${rows.length === 1 ? '' : 's'} in use.`}
        </Text>
        <Button icon={<ReloadOutlined />} onClick={() => void refresh()} loading={loading}>
          Refresh
        </Button>
      </Space>

      {loading && rows.length === 0 ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : rows.length === 0 ? (
        <Empty description="No skills configured." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
          }}
        >
          {rows.map((r) => (
            <div
              key={r.category}
              style={{
                background: isDark ? colors.slate[800] : '#FFFFFF',
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ fontSize: 14, fontWeight: 600 }}>{r.category}</Text>
                <Tooltip title="Rename across all rows">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setRenameSource(r.category)}
                  />
                </Tooltip>
              </div>
              <Space size={6} wrap>
                <StatusPill tone="ink">
                  {r.skillCount} skill{r.skillCount === 1 ? '' : 's'}
                </StatusPill>
                <StatusPill tone={r.activeCount === r.skillCount ? 'green' : 'amber'}>
                  {r.activeCount} active
                </StatusPill>
              </Space>
            </div>
          ))}
        </div>
      )}

      {renameSource != null && (
        <RenameSkillCategoryModal
          source={renameSource}
          onClose={() => setRenameSource(null)}
          onDone={async () => {
            setRenameSource(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function RenameSkillCategoryModal({
  source,
  onClose,
  onDone,
}: {
  source: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      open
      title={`Rename "${source}" across all rows`}
      okText="Rename"
      okButtonProps={{ disabled: !target.trim() || target.trim() === source }}
      onCancel={onClose}
      confirmLoading={saving}
      onOk={async () => {
        const to = target.trim();
        if (!to || to === source) return;
        setSaving(true);
        try {
          const r = await renameSkillCategory(source, to);
          void message.success(`Renamed across ${r.affected} skill${r.affected === 1 ? '' : 's'}.`);
          await onDone();
        } catch (err) {
          void message.error((err as Error).message);
        } finally {
          setSaving(false);
        }
      }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12.5 }}>
        Every <code>ai.AiSkill</code> row whose category equals exactly{' '}
        <b>{source}</b> will be updated. Audit fields (LastUpdatedDate / UpdatedBy) are stamped on each touched row.
      </Text>
      <Input
        placeholder="New category name"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        autoFocus
      />
    </Modal>
  );
}
